<script setup lang="ts">
import { computed } from "vue";

import { getClasses } from "~/js/composables/classes";
import { Analytics } from "~/js/interfaces/analytics";

import IconFa from "~/vue/_teammasters/atoms/icons/IconFa.vue";

interface Props {
    content?: string | null;
    disabled?: boolean;
    ga?: Analytics | null;
    showIcon?: boolean | null;
    theme?: "light" | "dark" | null;
    type?: "a" | "button" | "div" | "submit" | null;
    url?: string | null;
    utilities?: string | null;
    utilitiesIcon?: string | null;
    variant?: "small" | "wide" | null;
}

const props = withDefaults(defineProps<Props>(), {
    content: "",
    disabled: false,
    showIcon: true,
    theme: null,
    type: "button",
    url: null,
    utilities: "",
    utilitiesContent: "",
    utilitiesIcon: "",
    variant: "small",
});

const classes = {
    _default: {
        _default:
            "flex items-center justify-center sm:whitespace-nowrap cursor-pointer",
        size: props.variant == "small" ? "w-fit" : "w-full",
        display: "rounded-full",
        padding: "px-6 py-3 space-x-2",
        text: "text-lg font-semibold font-primary",
        focus: "focus-visible:ring-2 focus-visible:ring-offset-white dark:ring-offset-black focus-visible:ring-offset-2 focus-visible:outline-none",
        transition: "transition-colors duration-200 transform-gpu ease-in-out",
    },
    light: {
        border: "border-2 border-transparent ",
        color: "bg-neutral-900 text-white",
        hover: "hover:bg-white hover:text-neutral-900 hover:border-neutral-900",
        focus: "focus-visible:ring-indigo",
    },
    dark: {
        border: "dark:border-neutral-900",
        color: "dark:bg-white dark:text-neutral-900",
        hover: "dark:hover:bg-neutral-900 dark:hover:text-white",
        focus: "dark:focus-visible:ring-yellow",
    },
};

const getType = () => {
    return props.type == "submit" ? "button" : props.type;
};

const attributes = computed(() => {
    if (props.type === "div") {
        return {}; // No attributes for div
    }

    return {
        title: props.ga?.label ?? props.content,
        "data-tag-category": props.ga?.category ?? "",
        "data-tag-action": props.ga?.action ?? "",
        "data-tag-label": props.ga?.label ?? "",
    };
});
</script>

<template>
    <component
        :is="getType()"
        :class="[
            getClasses(classes._default),
            getClasses(classes.light),
            getClasses(classes.dark),
            utilities,
        ]"
        :data-theme="theme"
        :href="url"
        :type="type"
        v-bind="attributes"
    >
        <span>
            {{ content }}
        </span>
        <span v-if="showIcon">
            <IconFa icon="fa-kit fa-manneke-right" :utilities="utilitiesIcon" />
        </span>
    </component>
</template>
